import { Capacitor } from '@capacitor/core';

import { Nullable } from '@/types/common';

export function isInCapacitorApp() {
  // Capacitor 5/6/7+
  if (typeof Capacitor.isNativePlatform === 'function') {
    return Capacitor.isNativePlatform();
  }
  // Older Capacitor fallback
  return !!window.Capacitor?.isNative || Capacitor.getPlatform() !== 'web';
}

export const isEmbeddedBrowser = () => {
  const ua = navigator.userAgent || '';

  const isKnownInApp =
    /(NAVER|KAKAOTALK|KakaoStory|DaumApps|FBAN|FBAV|Instagram|Messenger|Line|MicroMessenger|Weibo|TikTok|BytedanceWebView|LinkedInApp|Snapchat|Pinterest|Twitter|WhatsApp|Telegram)/i.test(
      ua
    );

  const isAndroidWebView =
    /; wv\)|\bwv\b/i.test(ua) || (/Android/i.test(ua) && /Version\/[\d.]+/i.test(ua) && !/Chrome/i.test(ua));

  const isIOSWebView = /iPhone|iPad|iPod/i.test(ua) && /AppleWebKit/i.test(ua) && !/Safari/i.test(ua);

  return isKnownInApp || isAndroidWebView || isIOSWebView;
};

// 모바일 어플 내에서 실행 중인지 확인
export function getPlatform() {
  return isInCapacitorApp() ? Capacitor.getPlatform() /* 'ios' | 'android' */ : 'web';
}

export const getBrowserAndDeviceInfo = () => {
  const ua = navigator.userAgent;

  let browser = 'Unknown';
  let os = 'Unknown';
  const deviceType = /Mobile|iPhone|Android/i.test(ua) ? 'Mobile' : 'Desktop';

  if (/chrome|crios/i.test(ua) && !/edge|edgios|opr|opera/i.test(ua)) {
    browser = 'Chrome';
  } else if (/safari/i.test(ua) && !/chrome|crios|chromium/i.test(ua)) {
    browser = 'Safari';
  } else if (/firefox|fxios/i.test(ua)) {
    browser = 'Firefox';
  } else if (/edg/i.test(ua)) {
    browser = 'Edge';
  } else if (/opr|opera/i.test(ua)) {
    browser = 'Opera';
  }

  if (/windows nt/i.test(ua)) {
    os = 'Windows';
  } else if (/mac os x/i.test(ua)) {
    os = 'macOS';
  } else if (/android/i.test(ua)) {
    os = 'Android';
  } else if (/iphone|ipad|ipod/i.test(ua)) {
    os = 'iOS';
  } else if (/linux/i.test(ua)) {
    os = 'Linux';
  }

  return {
    browser,
    deviceType,
    os,
    ua,
    summary: `Browser: ${browser}, Device: ${deviceType}, OS: ${os}, UA: ${ua}`,
  };
};

export const isAppleDevice = () => {
  return /iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent);
};

export const classifyDeviceType = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('android')) {
    return 'a';
  } else if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
    return 'i';
  } else if (userAgent.includes('mac')) {
    return 'm';
  } else if (userAgent.includes('win')) {
    return 'w';
  } else {
    return 'u';
  }
};

export const isMobileBrowser = /iphone|ipad|ipod|android/i.test(navigator.userAgent.toLowerCase());

export const replaceMicLabel = (deviceLabel) => {
  const deviceType = classifyDeviceType();
  const isMac = deviceType === 'm';

  if (deviceLabel.includes('AirPods')) {
    return 'AirPods';
  } else if (deviceLabel.includes('MacBook Pro')) {
    const locale = localStorage.getItem('locale');
    if (locale === 'ko') {
      return 'MacBook Pro 마이크';
    } else if (locale === 'ja') {
      return 'MacBook Pro マイク';
    } else {
      return 'MacBook Pro Microphone';
    }
  } else if (deviceLabel.trim() === '기본값') {
    return '기본 마이크';
  } else if (deviceLabel.trim() === 'Default') {
    return 'Default Microphone';
  } else if (deviceLabel.trim() === 'デフォルト' || deviceLabel.trim() === '基本') {
    return '基本マイク';
  } else if (deviceLabel.includes('Galaxy Buds4 Pro')) {
    return 'Galaxy Buds4 Pro';
  } else if (deviceLabel.includes('Galaxy Buds4')) {
    return 'Galaxy Buds4';
  } else if (deviceLabel.includes('Galaxy Buds3 Pro')) {
    return 'Galaxy Buds3 Pro';
  } else if (deviceLabel.includes('Galaxy Buds3')) {
    return 'Galaxy Buds3';
  } else if (deviceLabel.includes('Galaxy Buds2 Pro')) {
    return 'Galaxy Buds2 Pro';
  } else if (deviceLabel.includes('Galaxy Buds2')) {
    return 'Galaxy Buds2';
  } else if (deviceLabel.includes('Galaxy Buds Pro')) {
    return 'Galaxy Buds Pro';
  } else if (deviceLabel.includes('Galaxy Buds')) {
    return 'Galaxy Buds';
  } else if (deviceLabel.includes('Britz Broad8')) {
    return 'Britz Broad8';
  } else if (deviceLabel.includes('Britz Broad7')) {
    return 'Britz Broad7';
  } else if (deviceLabel.includes('Britz Broad6')) {
    return 'Britz Broad6';
  } else if (deviceLabel.includes('Britz Broad5')) {
    return 'Britz Broad5';
  } else if (deviceLabel.includes('Britz Broad4')) {
    return 'Britz Broad4';
  } else if (deviceLabel.includes('Britz Broad3')) {
    return 'Britz Broad3';
  } else if (deviceLabel.includes('Britz Broad2')) {
    return 'Britz Broad2';
  } else if (deviceLabel.includes('Britz Broad1')) {
    return 'Britz Broad1';
  } else if (deviceLabel.includes('Britz Broad')) {
    return 'Britz Broad';
  } else if (deviceLabel.includes('Samsung') && deviceLabel.includes('Earphones')) {
    return 'Samsung Earphones';
  } else if (!isMac && deviceLabel.includes('Speakerphone')) {
    return 'Built-in microphone';
  } else {
    return deviceLabel.replace(/\s*\(.*?\)\s*$/, '');
  }
};

interface IUniqueDevice {
  id: string;
  label: string;
  default: boolean;
  groupId: string;
}

export const isAndroid: boolean = /android/i.test(navigator.userAgent.toLowerCase());

export function getUniqueDevices(devices: MediaDeviceInfo[]): Record<string, IUniqueDevice[]> {
  const deviceType = classifyDeviceType();
  const uniqueKeys: string[] = [];

  const defaultDevices = devices.reduce(
    (acc, { deviceId, label, kind, groupId }) => {
      if (deviceId === 'default' && !/\bvirtual\b/i.test(label)) {
        const mediaKey = kind === 'audioinput' ? 'input' : 'output';
        acc[mediaKey] = { groupId, label: label.replace(/^.*? - /, '') };
      }
      return acc;
    },
    { output: { groupId: '', label: '' }, input: { groupId: '', label: '' } }
  );

  const isUsbInList: boolean = devices.some(({ label }) => label.toLowerCase().includes('usb'));

  return devices.reduce(
    (acc, { kind, deviceId, label, groupId }) => {
      if (
        !label ||
        (kind !== 'audioinput' && kind !== 'audiooutput') ||
        ['communications', 'default'].includes(deviceId) ||
        (isAndroid &&
          isUsbInList &&
          kind === 'audioinput' &&
          (label.toLowerCase().includes('headset earpiece') || label.toLowerCase().includes('speakerphone')))
      ) {
        return acc;
      }

      // 중복 제거 키
      const uniqueKey = deviceType === 'm' ? groupId : deviceId;
      // 입출력 장치 키
      const mediaKey = kind === 'audioinput' ? 'input' : 'output';

      if (!uniqueKeys.includes(uniqueKey) && deviceId !== 'default') {
        uniqueKeys.push(uniqueKey);
        acc[mediaKey].push({
          id: deviceId,
          label,
          default: defaultDevices[mediaKey].groupId === groupId && defaultDevices[mediaKey].label === label,
          groupId,
        });
      }

      return acc;
    },
    { output: [] as IUniqueDevice[], input: [] as IUniqueDevice[] }
  );
}

export async function getInoutDevices(
  previousDevices: Nullable<MediaDeviceInfo[]> = null,
  newDevices: Nullable<MediaDeviceInfo[]> = null
) {
  // 0) 플랫폼 정보 가져오기
  const deviceType = classifyDeviceType();

  // 1) 모든 디바이스 나열 및 중복제거
  const devices = getUniqueDevices(newDevices || (await navigator.mediaDevices.enumerateDevices()));

  // 2) default 항목이 하나도 없으면, 각 리스트 첫 번째를 default로 설정
  ['output', 'input'].forEach((key) => {
    const list = devices[key];
    if (list.length && !list.some((d) => d.default)) {
      const fallback = list.find((d) => !/\bvirtual\b/i.test(d.label) && !/blackhole/i.test(d.label));

      if (
        /android/i.test(navigator.userAgent.toLowerCase()) &&
        key === 'input' &&
        list.some(({ label }) => label.toLowerCase().includes('usb'))
      ) {
        const inx: number = list.findIndex(({ label }) => label.toLowerCase().includes('usb'));
        if (inx > -1) {
          list[inx].default = true;
        }
      } else if (fallback) {
        fallback.default = true;
      } else {
        list[0].default = true;
      }
    }
  });

  // 3) initialGain 계산 헬퍼
  const computeInitialGain = ({ default: isDefault, label }) => {
    const lowLabel = label.toLowerCase();
    if (deviceType === 'a') {
      // Android
      return getInitialGain(label, 0.8);
    } else if (deviceType === 'i') {
      // iOS (iPhone/iPad)
      return lowLabel.includes('iphone')
        ? getInitialGain(label) // 기본 파라미터 사용
        : getInitialGain(label, 1.0);
    } else {
      // 그 외 플랫폼 (Mac, Win, Unknown)
      return isDefault ? getInitialGain(label, 7.0) : getInitialGain(label, 1.0);
    }
  };

  // 4) previousDevices 비교를 위한 ID 세트
  const prevOutputIds = new Set((previousDevices?.output || []).map((d) => d.id));
  const prevInputIds = new Set((previousDevices?.input || []).map((d) => d.id));

  // 5) 최종 리스트 생성: newDevice + initialGain 추가
  const outputList = devices.output
    .map((d) => ({
      id: d.id,
      label: d.label,
      default: d.default,
      newDevice: previousDevices ? !prevOutputIds.has(d.id) : false,
      initialGain: computeInitialGain(d),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const inputList = devices.input
    .map((d) => ({
      id: d.id,
      label: d.label,
      default: d.default,
      customLabel: '',
      isConnected: true,
      newDevice: previousDevices ? !prevInputIds.has(d.id) : false,
      initialGain: computeInitialGain(d),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return {
    output: outputList,
    input: deduplicateDeviceList(inputList),
  };
}

// 라벨별 중복 방지 및 디폴트 우선시하기
const deduplicateDeviceList = (deviceList) => {
  const deviceMap = new Map<string, (typeof deviceList)[number]>();
  for (const deviceItem of deviceList) {
    const deviceKey = deviceItem.label;
    const prev = deviceMap.get(deviceKey);
    if (!prev || (deviceItem.default && !prev.default)) {
      deviceMap.set(deviceKey, deviceItem);
    }
  }
  return Array.from(deviceMap.values());
};

function getInitialGain(label, additionalGain = 1.0) {
  if (['speakerphone', 'headset earpiece'].includes(label.toLowerCase())) {
    return 1.7;
  } else if (label.toLowerCase().includes('mac')) {
    return 12.5;
  } else if (label.toLowerCase().includes('airpods')) {
    return 2.0;
  } else if (label.toLowerCase().includes('iphone')) {
    return 22.0;
  } else if (label.toLowerCase().includes('ipad')) {
    return 60.0;
  } else if (label.toLowerCase().includes('realtek')) {
    return 7.0;
  } else if (label.toLowerCase().includes('microphone array')) {
    return 7.0;
  } else if (label.toLowerCase().includes('usb')) {
    return 0.7;
  } else if (additionalGain && Number(additionalGain)) {
    return additionalGain;
  } else {
    return 15.0;
  }
}
